<template>
    <PageHeader :title="headerTitle" :back-to="'/customer/detail/' + customerId"/>

    <div v-if="summary" class="ctx-summary-card">
        <div class="ctx-summary-header">
            <div class="ctx-summary-header-text">
                <span class="ctx-summary-title">{{ $t('pages.customerTransaction.summaryTitle') }}</span>
                <span v-if="customer" class="ctx-summary-customer">{{ customer.full_name }}</span>
            </div>
        </div>

        <div class="ctx-summary-metrics">
            <div class="ctx-metric ctx-metric--total">
                <div class="ctx-metric-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="2" y="6" width="20" height="13" rx="2"/>
                        <line x1="2" y1="11" x2="22" y2="11"/>
                    </svg>
                </div>
                <span class="ctx-metric-label">{{ $t('common.transaction.summary.total') }}</span>
                <span class="ctx-metric-value">{{ formatTRY(summary.total_amount) }}</span>
            </div>

            <div class="ctx-metric ctx-metric--paid">
                <div class="ctx-metric-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                    </svg>
                </div>
                <span class="ctx-metric-label">{{ $t('common.transaction.summary.paid') }}</span>
                <span class="ctx-metric-value">{{ formatTRY(summary.paid_amount) }}</span>
            </div>

            <div class="ctx-metric ctx-metric--remaining">
                <div class="ctx-metric-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                    </svg>
                </div>
                <span class="ctx-metric-label">{{ $t('common.transaction.summary.remaining') }}</span>
                <span class="ctx-metric-value">{{ formatTRY(summary.remaining_amount) }}</span>
            </div>
        </div>
    </div>

    <custom-table
        :title="$t('pages.transaction.title')"
        rowKey="id"
        :items="tableData"
        :loading="loading"
        :columns="fields"
        :actions="actions"
        :pagination="pagination"
        :sortMeta="pagination.sort || []"
        changeUrlPage
        @action="handleClickAction"
        @changeTable="handleTableChange"
        @sort="handleSort"
    >
        <template v-slot:direction="{ row: record }">
            <span class="tx-direction" :class="record.direction_id === 1 ? 'tx-direction--in' : 'tx-direction--out'">
                <svg v-if="record.direction_id === 1" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="6 9 12 15 18 9"/>
                </svg>
                <svg v-else width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="18 15 12 9 6 15"/>
                </svg>
                {{ $t('common.transaction.direction.' + record.direction_id) }}
            </span>
        </template>

        <template v-slot:source="{ row: record }">
            <div v-if="record.source_type" class="tx-source">
                <span class="tx-source-label">{{ sourceLabel(record.source_type) }}</span>
                <span class="tx-source-id">#{{ record.source_id }}</span>
            </div>
            <span v-else class="tx-badge tx-badge--manual">{{ $t('common.transaction.badge.manualPill') }}</span>
        </template>

        <template v-slot:amounts="{ row: record }">
            <div class="tx-amounts">
                <div class="tx-amounts-total">{{ formatTRY(record.total_amount) }}</div>
                <div v-if="record.status_id !== 2" class="tx-amounts-line2">
                    <span class="tx-amounts-paid">{{ formatTRY(record.paid_amount) }}</span>
                    <span class="tx-amounts-sep">/</span>
                    <span class="tx-amounts-remaining">{{ formatTRY(record.remaining_amount) }}</span>
                </div>
            </div>
        </template>

        <template v-slot:status="{ row: record }">
            <span class="tx-pill" :class="'tx-pill--' + record.status_id">
                {{ $t('common.transaction.status.' + record.status_id) }}
            </span>
        </template>

        <template v-slot:created_at="{ row: record }">
            {{ formatDate(record.created_at) }}
        </template>

        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <el-tooltip :content="$t('common.transaction.actions.openPayments')" placement="top">
                    <button class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm" @click="openPayments(record)">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/finance/fin010.svg"/>
                        </span>
                    </button>
                </el-tooltip>
            </div>
        </template>
    </custom-table>
</template>

<script>
import CustomTable from "@/components/custom-table";
import PageHeader from "@/components/page-header/PageHeader.vue";
import transactionService from "@/components/payment/transactionService";
import { resolveSourceLabelKey } from "@/core/services/sourceResolver";
import { normalizePagination } from "@/core/services/Helper";

export default {
    name: "customer-transaction",
    components: { CustomTable, PageHeader },
    data() {
        return {
            customer: null,
            summary: null,
            tableData: [],
            pagination: { current: 1, pageSize: 10, total: 0 },
            loading: false,
            filterParameters: { sort: 'id:desc' },
            fields: [
                { name: this.$t("pages.transaction.cols.id"), key: "id", class: '', sortable: true },
                { name: this.$t("pages.transaction.cols.direction"), sortingField: "direction_id", sortable: true, scopedSlots: { customRender: "direction" } },
                { name: this.$t("pages.transaction.cols.source"), sortingField: "source_type", sortable: true, scopedSlots: { customRender: "source" } },
                { name: this.$t("pages.transaction.cols.amounts"), sortingField: "total_amount", sortable: true, scopedSlots: { customRender: "amounts" } },
                { name: this.$t("pages.transaction.cols.status"), sortingField: "status_id", sortable: true, scopedSlots: { customRender: "status" } },
                { name: this.$t("pages.transaction.cols.createdAt"), sortingField: "created_at", sortable: true, scopedSlots: { customRender: "created_at" } },
                { name: this.$t("common.action"), key: "action", scopedSlots: { customRender: "actions" } },
            ],
            actions: [
                { name: "refresh", icon: "bi-arrow-repeat", label: "btn.refresh" },
            ],
        };
    },
    computed: {
        customerId() {
            return this.$route.params.id;
        },
        headerTitle() {
            if (this.customer) {
                return this.customer.full_name + ' · ' + this.$t('pages.customerTransaction.title');
            }
            return this.$t('pages.customerTransaction.title');
        },
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t('pages.customerTransaction.title'), []);
        this.fetchCustomer();
        this.refreshTable();
    },
    methods: {
        formatTRY(amount) {
            const n = parseFloat(amount);
            if (isNaN(n)) return '-';
            return n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });
        },
        formatDate(d) {
            if (!d) return '-';
            const dt = new Date(d);
            if (isNaN(dt.getTime())) return d;
            return dt.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                + ' · ' + dt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        },
        sourceLabel(type) {
            const key = resolveSourceLabelKey(type) || ('common.transaction.sourceModules.' + type);
            const t = this.$t(key);
            return t === key ? type : t;
        },
        async fetchCustomer() {
            try {
                await this.$store.dispatch('customer/show', { id: this.customerId });
                this.customer = this.$store.state.customer.save?.data || null;
            } catch (_) { /* empty */ }
        },
        async refreshTable(page) {
            this.loading = true;
            const url = this.endpoints['company_customer_transaction'].replace(':customerId', this.customerId);
            const params = {
                page: page?.current,
                page_limit: page?.pageSize,
                ...this.filterParameters,
            };
            try {
                const res = await this.axios.get(url, { params });
                const body = res.data || {};
                this.tableData = body.data || [];
                this.pagination = normalizePagination(body.meta);
                this.summary = body.summary || null;
            } finally {
                setTimeout(() => { this.loading = false; }, 200);
            }
        },
        handleClickAction(name) {
            if (name === 'refresh') this.refreshTable();
        },
        handleTableChange(pagination, filterData) {
            const currentSort = this.filterParameters.sort || 'id:desc';
            this.filterParameters = { ...filterData, sort: currentSort };
            this.refreshTable(pagination);
        },
        handleSort({ columnName, order }) {
            this.filterParameters = {
                ...this.filterParameters,
                sort: `${columnName}:${order}`,
            };
            this.refreshTable(this.pagination);
        },
        openPayments(record) {
            transactionService.open({
                transactionId: record.id,
                onChange: () => this.refreshTable(this.pagination),
            });
        },
    },
};
</script>

<style scoped>
.ctx-summary-card {
    margin: 12px 0 18px;
    padding: 22px 24px;
    border-radius: 14px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.04);
}

.ctx-summary-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
}

.ctx-summary-header-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.ctx-summary-title {
    font-size: 11px;
    font-weight: 700;
    color: #8e8e93;
    text-transform: uppercase;
    letter-spacing: 0.6px;
}

.ctx-summary-customer {
    font-size: 16px;
    font-weight: 700;
    color: #1c1c1e;
}

.ctx-summary-metrics {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
}

.ctx-metric {
    position: relative;
    padding: 18px 18px 18px 20px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.025);
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow: hidden;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.ctx-metric::before {
    content: '';
    position: absolute;
    left: 0;
    top: 14px;
    bottom: 14px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: currentColor;
    opacity: 0.45;
}

.ctx-metric-icon {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: inherit;
    overflow: hidden;
}

.ctx-metric-icon::before {
    content: '';
    position: absolute;
    inset: 0;
    background: currentColor;
    opacity: 0.14;
}

.ctx-metric-icon svg {
    position: relative;
    z-index: 1;
}

.ctx-metric-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #8e8e93;
}

.ctx-metric-value {
    font-size: 22px;
    font-weight: 700;
    color: #1c1c1e;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
    letter-spacing: -0.2px;
}

.ctx-metric--total { color: #6b7280; }
.ctx-metric--paid { color: #28a745; }
.ctx-metric--remaining { color: var(--panel-accent-text, #007AFF); }

.ctx-metric--paid .ctx-metric-value { color: #28a745; }
.ctx-metric--remaining .ctx-metric-value { color: var(--panel-accent-text, #007AFF); }


/* Dark theme */
html.dark .ctx-summary-card {
    background: #15171C;
    border-color: rgba(255, 255, 255, 0.07);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.25);
}

html.midnight .ctx-summary-card {
    background: #1A1714;
    border-color: rgba(255, 255, 255, 0.07);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.25);
}

html.dark .ctx-summary-title,
html.midnight .ctx-summary-title { color: #9A9CAE; }

html.dark .ctx-summary-customer,
html.midnight .ctx-summary-customer { color: #EAE6E1; }

html.dark .ctx-metric {
    background: rgba(255, 255, 255, 0.025);
    border-color: rgba(255, 255, 255, 0.05);
}

html.midnight .ctx-metric {
    background: rgba(255, 255, 255, 0.02);
    border-color: rgba(255, 255, 255, 0.05);
}

html.dark .ctx-metric-label,
html.midnight .ctx-metric-label { color: #9A9CAE; }

html.dark .ctx-metric-value,
html.midnight .ctx-metric-value { color: #EAE6E1; }

html.dark .ctx-metric--total,
html.midnight .ctx-metric--total { color: #9CA3AF; }

html.dark .ctx-metric--paid,
html.midnight .ctx-metric--paid { color: #34D399; }
html.dark .ctx-metric--paid .ctx-metric-value,
html.midnight .ctx-metric--paid .ctx-metric-value { color: #34D399; }

/* Cream theme */
html.cream .ctx-summary-card {
    background: #FEFCF8;
    border-color: rgba(61, 56, 50, 0.1);
    box-shadow: 0 1px 3px rgba(61, 56, 50, 0.04), 0 8px 24px rgba(61, 56, 50, 0.05);
}

html.cream .ctx-summary-title { color: #847C72; }
html.cream .ctx-summary-customer { color: #3D3832; }

html.cream .ctx-metric {
    background: rgba(61, 56, 50, 0.04);
    border-color: rgba(61, 56, 50, 0.08);
}

html.cream .ctx-metric-label { color: #847C72; }
html.cream .ctx-metric-value { color: #3D3832; }

html.cream .ctx-metric--total { color: #847C72; }
html.cream .ctx-metric--paid,
html.cream .ctx-metric--paid .ctx-metric-value { color: #059669; }
html.cream .ctx-metric--remaining,
html.cream .ctx-metric--remaining .ctx-metric-value { color: #059669; }

@media (max-width: 720px) {
    .ctx-summary-metrics {
        grid-template-columns: 1fr;
    }
    .ctx-summary-card {
        padding: 18px;
    }
    .ctx-metric-value {
        font-size: 19px;
    }
}


/* Table cell styles (copied from transaction index for consistency) */
.tx-direction {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
}

.tx-direction--in { background: rgba(40, 167, 69, 0.12); color: #28a745; }
.tx-direction--out { background: rgba(239, 68, 68, 0.12); color: #ef4444; }

html.dark .tx-direction--in,
html.midnight .tx-direction--in { color: #34D399; }
html.dark .tx-direction--out,
html.midnight .tx-direction--out { color: #F87171; }

.tx-source { display: flex; flex-direction: column; gap: 1px; line-height: 1.2; }
.tx-source-label { font-weight: 600; font-size: 12.5px; }
.tx-source-id { font-size: 11px; color: #8e8e93; font-variant-numeric: tabular-nums; }
html.dark .tx-source-id, html.midnight .tx-source-id { color: #9A9CAE; }

.tx-badge { display: inline-flex; padding: 2px 8px; border-radius: 999px; font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; }
.tx-badge--manual { background: rgba(99, 102, 241, 0.12); color: #6366f1; }
html.dark .tx-badge--manual, html.midnight .tx-badge--manual { color: #818CF8; }

.tx-amounts { display: flex; flex-direction: column; gap: 2px; min-width: 130px; font-variant-numeric: tabular-nums; }
.tx-amounts-total { font-weight: 700; font-size: 13.5px; }
.tx-amounts-line2 { display: flex; align-items: center; gap: 4px; font-size: 11.5px; color: #8e8e93; }
.tx-amounts-paid { color: #28a745; font-weight: 600; }
.tx-amounts-sep { opacity: 0.55; }
.tx-amounts-remaining { color: #d97706; font-weight: 600; }
html.dark .tx-amounts-line2, html.midnight .tx-amounts-line2 { color: #9A9CAE; }
html.dark .tx-amounts-paid, html.midnight .tx-amounts-paid { color: #34D399; }
html.dark .tx-amounts-remaining, html.midnight .tx-amounts-remaining { color: #FBBF24; }

.tx-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
}
.tx-pill::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.tx-pill--0 { background: rgba(107, 114, 128, 0.15); color: #6b7280; }
.tx-pill--1 { background: rgba(245, 158, 11, 0.15); color: #d97706; }
.tx-pill--2 { background: rgba(40, 167, 69, 0.15); color: #28a745; }
.tx-pill--3 { background: rgba(59, 130, 246, 0.15); color: #3b82f6; }
.tx-pill--4 { background: rgba(239, 68, 68, 0.15); color: #ef4444; }

html.dark .tx-pill--0, html.midnight .tx-pill--0 { color: #9CA3AF; }
html.dark .tx-pill--1, html.midnight .tx-pill--1 { color: #FBBF24; }
html.dark .tx-pill--2, html.midnight .tx-pill--2 { color: #34D399; }
html.dark .tx-pill--3, html.midnight .tx-pill--3 { color: #60A5FA; }
html.dark .tx-pill--4, html.midnight .tx-pill--4 { color: #F87171; }
</style>
